<template>
  <div>
    <breadcrumb :items="breadcrumbs"></breadcrumb>
    <v-card color="transparent" flat>
      <v-toolbar color="transparent" flat>
        <v-toolbar-title class="font-weight-bold">{{ title }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn color="primary" class="mr-2" @click="print">
          <v-icon class="mr-2" small dark> mdi-printer-outline </v-icon>
          {{ texts.printButton }}
        </v-btn>
        <v-btn color="primary" @click="download">
          <v-icon class="mr-2" small dark> mdi-tray-arrow-down </v-icon>
          {{ texts.downloadButton }}
        </v-btn>
      </v-toolbar>
      <v-card outlined class="v-card__body mb-4">
        <v-card color="transparent px-4 py-4" class="group-details" flat>
          <v-row no-gutters class="group-rows" v-for="(row, idx) in groups[$route.params.trait].layout" :key="$route.params.trait + idx">
            <v-col cols="2" class="group-item" v-for="item in row" :key="item">
              <v-list>
                <v-list-item>
                  <v-list-item-content>
                      <v-list-item-subtitle v-if="item === 'groupCode'">{{ groups[$route.params.trait].code }}</v-list-item-subtitle>
                      <v-list-item-subtitle v-else>{{ texts.labels[item] }}</v-list-item-subtitle>
                      <v-list-item-title class="font-weight-bold text-capitalize">{{ groupDetails[item] || "-" }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>
        </v-card>
      </v-card>
      <v-toolbar color="transparent" flat>
        <v-toolbar-title class="font-weight-bold">{{ texts.associatedAnimals }} ({{ groupDetails.noOfAnimals }})</v-toolbar-title>
      </v-toolbar>
      <v-card outlined class="v-card__body mb-4">
        <v-data-table
          :headers="headers"
          :items="items"
          :search="search"
          hide-default-footer
          :loading="loading"
          :options.sync="tableOptions"
          :server-items-length="itemCount"
          loading-text="$t('loadingPleaseWait')"
        >
          <template v-slot:top>
            <div class="d-flex mt-5">
              <v-col cols="4" class="pl-0">
                <v-text-field
                  prepend-inner-icon="mdi-magnify"
                  outlined
                  height="5px"
                  placeholder="Search"
                  dense
                  v-model="search"
                  class="shrink"
                ></v-text-field>
              </v-col>
              <v-spacer></v-spacer>
              <div class="py-1 title">
                {{ from }} - {{ to }} of {{ itemCount }}
              </div>
              <v-btn
                class="mx-2"
                fab
                small
                outlined
                color="primary"
                :disabled="tableOptions.page <= 1"
                @click="pageChange(false)"
              >
                <v-icon dark> mdi-chevron-left </v-icon>
              </v-btn>
              <v-btn
                class="mx-2"
                fab
                small
                outlined
                color="primary"
                :disabled="tableOptions.page >= pageCount"
                @click="pageChange(true)"
              >
                <v-icon dark> mdi-chevron-right </v-icon>
              </v-btn>
              <v-btn
                class="mx-2"
                fab
                small
                outlined
                color="primary"
              >
                <v-icon dark> mdi-view-grid-outline </v-icon>
              </v-btn>
              <v-btn
                class="mx-2"
                fab
                small
                outlined
                color="primary"
              >
                <v-icon dark> mdi-format-list-bulleted </v-icon>
              </v-btn>
            </div>
          </template>

          <template v-slot:header.birthWeight="{}">
                <span class="">{{ weightUnit === 'LBS'
                  ? $t('geneticsAndBreeding.birthWeightLb')
                  : $t('geneticsAndBreeding.birthWeightKg') }}</span>
          </template>
          <template v-slot:header.weaningWeight="{}">
              <span class="">{{ weightUnit === 'LBS'
                  ? $t('geneticsAndBreeding.weaningWeightLb')
                  : $t('geneticsAndBreeding.weaningWeightKg') }}</span>
          </template>
          <template v-slot:header.yearlingWeight="{}">
                <span class="">{{ weightUnit === 'LBS'
                    ? $t('geneticsAndBreeding.yearlingWeightLb')
                    : $t('geneticsAndBreeding.yearlingWeightKg') }}</span>
            </template>
            <template v-slot:header.carcass_weight="{}">
                <span class="">{{ weightUnit === 'LBS'
                    ? $t('geneticsAndBreeding.carcassWeightLb')
                    : $t('geneticsAndBreeding.carcassWeightKg') }}</span>
            </template>
          <template v-if="items.length > 0" v-slot:body="{ items }">
            <tbody>
              <tr
                v-for="(item, index) in items"
                :key="index"
                style="position: relative; width: 0; height: 0"
              >
                <td>{{ item.animalNumber }}</td>
                <td>{{ item.name }}</td>
                <td>{{ item.tagNumber }}</td>
                <td class="capitalize">{{ item.gender  }}</td>
                <td>{{ item.birthDate }}</td>
                <td>{{ item.birth_weight }}</td>
                <td>{{ item.weaning_weight }}</td>
                <td>{{ item.yearling_weight }}</td>
                <td>{{ item.carcass_weight }}</td>
                <td>
                  <v-chip
                    class="status-chip"
                    color="success"
                    outlined>
                    {{ item.status || "N/A" }}
                  </v-chip>
                </td>
              </tr>
            </tbody>
          </template>
        </v-data-table>
      </v-card>
    </v-card>
  </div>
</template>
<script>
/* eslint-disable */
import axios from 'axios';
import {
  getBirthWeightContemporaryGroupById,
  getAnimalListByIdandTrait
} from "@/graphql/EPD.gql";

export default {
  computed: {
    title() {
      return `Detail View of ${this.groups[this.$route.params.trait].title} CG (${this.$route.params.groupCode})`;
    }
  },
  data() {
    return {
      weightUnit: "LBS",
      breadcrumbs: [
          {
            text: this.$t('dashboard'),
            disabled: false,
            to: "/",
          },
          {
            text: this.$t('Genetics & Breeding'),
            disabled: false,
            link: true,
            exact: true,
            to: { name: "Genetics" },
          },
          {
            text: this.$t('Contemporary Group Reports'),
            disabled: false,
            link: true,
            exact: true,
            to: { name: "cgReports" }
          },
          {
            text: this.$t('Detailed view'),
            disabled: true
          }
        ],
        texts: {
          printButton: "Print",
          downloadButton: "Download",
          associatedAnimals: "Associated Animals",
          labels: {
            groupName: "Group Name",
            noOfAnimals: "No. of Animals",
            birthYear: "Birth Year",
            birthSex: "Birth Sex",
            breederHerdCode: "Breeder Herd Code",
            birthManagementCode: "Birth Management Code",
            seasonOfBirth: "Season of Birth",
            primaryBreed: "Primary Breed",
            secondaryBreed: "Secondary Breed",
            conceptionMethod: "Conception Method",
            weaningWeight: "Weaning Weight (KG)",
            dateWeight: "Date Weight",
            weaningSex: "Weaning Sex",
            carcassSex: "Carcass Sex",
            yearlingSex: "Yearling Sex",
            managementPastureCode: "Management/Pasture Code",
            managementFeedingBatchCode: "Management/Feeding Batch Code",
            daysOnFeed: "Days on Feed",
            fatThickness: "Fat Thickness (cm)",
            managementPenFeedingBatch: "Management/Pen/Feeding Batch",
            dateOnFeed: "Date on Feed",
            breedOfDom: "Breed of Dam",
            harvestDate: "Harvest Date",
            gradingDate: "Grading Date"
          },
        },
        groups: {
          birthWeight: {
            title: "Birth Weight",
            code: "Birth (CG) Code",
            layout: [
              ['groupCode', 'groupName', 'noOfAnimals', 'birthYear', 'birthSex', 'breederHerdCode'],
              ['birthManagementCode', 'seasonOfBirth', 'primaryBreed', 'secondaryBreed', 'conceptionMethod']
            ]
          },
          weaningWeight: {
            title: "Weaning Weight",
            code: "Birth (CG) Code",
            layout: [
              ['groupCode', 'groupName', 'noOfAnimals', 'weaningWeight', 'dateWeight'],
              ['weaningSex', 'primaryBreed', 'secondaryBreed', 'conceptionMethod', 'managementPastureCode']
            ]
          },
          yearlingWeight: {
            title: "Yearling Weight",
            code: "Weaning (CG) Group Code",
            layout: [
              ['groupCode', 'groupName', 'noOfAnimals', 'yearlingWeight', 'dateWeight'],
              ['yearlingSex', 'managementFeedingBatchCode']
            ]
          },
          carcassWeight: {
            title: "Carcass Weight",
            code: "Weaning/Yearling (CG) Group Code",
            layout: [
              ['groupCode', 'groupName', 'noOfAnimals', 'managementPenFeedingBatch', 'dateOnFeed'],
              ['breedOfDom', 'fatThickness', 'daysOnFeed', 'harvestDate', 'gradingDate', 'carcassSex']
            ]
          }
        },
        headers: [
        {
              text: "Animal ID",
              value: "animal_id",
            },
        {
              text: "Animal Name",
              value: "name",
            },
            {
              text: "Tag Number",
              value: "tagNumber",
            },
            {
              text: "Gender",
              value: "gender",
            },
            {
              text: "Birth Date",
              value: "birthDate",
            },
            {
              text: "Birth Weight",
              value: "birth_weight",
            },
            {
              text: "Weaning Weight",
              value: "weaning_weight",
            },
            {
              text: "Yearling Weight",
              value: "yearling_weight",
            },
            {
              text: "Carcass Weight",
              value: "carcass_weight",
            },
          { text: "Status" 
        }
        ],
        items: [],
        from: 0,
        to: 0,
        tableOptions: {
          page: 1,
          itemsPerPage: 5
        },
        loading: false,
        search: "",
        pageCount: 1,
        itemCount: 0,
        groupDetails: {}
      }
    },
    created() {
      this.getGroupData();
    },
    methods: {
      snakeCaseToCamelCase (key) {
        return key.toLowerCase().split("_").map(((part, i) => (i > 0) ? part.charAt(0).toUpperCase() + part.slice(1) : part)).join("");
      },
      getMonthName(monthNumber) {
        return new Date(`1997-${monthNumber}-1`).toLocaleString('en-US', {month: 'long'});
      },
      getDateRange(startYear, endYear) {
        return `${new Date(startYear).toLocaleDateString('en-US')} to ${new Date(endYear).toLocaleDateString('en-US')}`;
      },
      async getGroupData() {

        const response = await this.$apollo.query({
          query: getBirthWeightContemporaryGroupById,
          fetchPolicy: "no-cache",
          variables: {
            id: +this.$route.params.id,
          },
        });

        if (response.data && response.data.getBirthWeightContemporaryGroupById && Object.keys(response.data.getBirthWeightContemporaryGroupById).length !== 0) {
          const groupData = {};
          Object.entries(response.data.getBirthWeightContemporaryGroupById).forEach(([key, item]) => {
            groupData[this.snakeCaseToCamelCase(key)] = item;
          });
          groupData.primaryBreed = `${groupData.primaryBreed} ${groupData.primaryBreedComposition} %`;
          groupData.secondaryBreed = `${groupData.secondaryBreed} ${groupData.secondaryBreedComposition} %`;

          switch(this.$route.params.trait) {
            case 'birthWeight':
              groupData.seasonOfBirth = `${this.getMonthName(groupData.seasonOfBirthFrom)} to ${this.getMonthName(groupData.seasonOfBirthTo)}`;
              groupData.birthYear = `${new Date(groupData.birthYearFrom).getFullYear()} to ${new Date(groupData.birthYearTo).getFullYear()}`;
              break;
            case 'weaningWeight':
              groupData.weaningCode = groupData.groupCode;
              groupData.groupCode = groupData.birthWeightContemporaryGroup?.group_code;
              groupData.weaningWeight = `${groupData.weaningWeightMin} to ${groupData.weaningWeightMax}`;
              groupData.dateWeight = this.getDateRange(groupData.dateWeightedFrom, groupData.dateWeightedTo);
              break;
            case 'yearlingWeight':
              groupData.yearlingWeight = groupData.groupCode;
              groupData.groupCode = groupData.weaningWeightContemporaryGroup?.group_code;
              groupData.yearlingWeight = `${groupData.yearlingWeightMin} to ${groupData.yearlingWeightMax}`;
              groupData.dateWeight = this.getDateRange(groupData.dateWeightedFrom, groupData.dateWeightedTo);
              break;
            case 'carcassWeight':
              groupData.carcassWeight = groupData.groupCode;
              if (groupData.weaningWeightContemporaryGroup) {
                groupData.groupCode = groupData.weaningWeightContemporaryGroup?.group_code;
              } else if (groupData.yearlingWeightContemporaryGroup) {
                groupData.groupCode = groupData.yearlingWeightContemporaryGroup?.group_code;
              }
              groupData.dateOnFeed = this.getDateRange(groupData.dateOnFeedFrom, groupData.dateOnFeedTo);
              groupData.harvestDate = this.getDateRange(groupData.harvestDateFrom, groupData.harvestDateTo);
              groupData.gradingDate = this.getDateRange(groupData.gradingDateFrom, groupData.gradingDateTo);
              groupData.fatThickness = `${groupData.fatThicknessMin} to ${groupData.fatThicknessMax}`;
              break;
          }

          this.groupDetails = {...groupData};
        } else {
          this.$notify({
            title: `$t('geneticsAndBreeding.errorFetchingContemporaryGrpMsg)`,
            text: res.data.message,
            type: "error",
          });
        }
      },
      async getAnimalsList() {
        this.loading = true;
        const res = await this.$apollo.query({
          query: getAnimalListByIdandTrait,
          fetchPolicy: "no-cache",
          variables: {
            animalListInput: {
              id: +this.$route.params.id,
              traitType: this.$route.params.trait,
              page: 1,
              limit: 10,
              search: "",
            }
          },
        });
        // const res = await axios.get(`animalGenetics/contemporaryGroup/${this.$route.params.trait}/${this.$route.params.id}/animals?page=${this.tableOptions.page}&limit=${this.tableOptions.itemsPerPage}&searchPhrase=${this.search}`);
        if (res.data.getAnimalListByIdandTrait  && Object.keys(res.data.getAnimalListByIdandTrait).length !== 0) {
          const result = res.data.getAnimalListByIdandTrait;
          this.weightUnit = result.weightUnit
          this.items = result.rows;
          this.groupDetails = {...this.groupDetails, noOfAnimals: result.count};

          this.itemCount = result.count;
          this.pageCount = Math.ceil(this.itemCount / this.tableOptions.itemsPerPage);
          this.from = (this.tableOptions.page - 1) * (this.tableOptions.itemsPerPage + 1);
          this.to = this.tableOptions.page * this.tableOptions.itemsPerPage - (this.tableOptions.itemsPerPage - result.rows.length);
        } else {
          this.$notify({
            title: `$t('geneticsAndBreeding.errorFetchingContemporaryGrpMsg)`,
            text: res.data.message,
            type: "error",
          });
        }
        this.loading = false;
      },
      pageChange(t) {
        if (t) {
          this.tableOptions.page = this.tableOptions.page + 1
          return
        } else {
          this.tableOptions.page = this.tableOptions.page - 1
          return
        }
      },
      print () {},
      download () {}
    },
    watch: {
      tableOptions: {
        handler() {
          this.getAnimalsList();
        },
        deep: true,
        immediate: true
      },
      search: {
        handler() {
          this.getAnimalsList();
        },
        deep: true
      }
    }
}
</script>

<style scoped>
.grey-hr {
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-bottom: none;
}

.input-label {
  color: #1E1E1E;
  font-size: 14px;
  margin-bottom: 8px;
  display: block;
}

.input-label i {
  font-size: 20px;
  margin-left: 8px;
}

.v-card__body {
  background-color: #ffffff;
  padding: 24px;
  margin: 16px;
}

.highlight {
  color: #FFB443;
}

.group-details > .group-rows:first-child {
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.group-rows > .group-item:first-child >>> .v-list-item:first-child {
  padding-left: 0 !important;
}

.group-rows > .group-item:first-child >>> .v-list-item:last-child {
  padding-right: 0 !important;
}

.capitalize {
  text-transform: capitalize;
}

.status-chip.v-chip.v-chip--outlined.v-chip.v-chip {
  background: #E5F8F1 !important;
}
</style>
